import { useEffect, useState } from 'react';
import { listAssetComments } from '../../../api';
import CommentPreviewButton from '../../../components/CommentPreviewButton';

type Translate = (key: string, options?: Record<string, unknown>) => string;
type AssetCategory = 'character' | 'scene' | 'prop';

interface AssetCommentButtonProps {
  projectId: string;
  category: AssetCategory;
  name: string;
  count: number;
  onClick: () => void;
  t: Translate;
}

// 资产评论按钮：负责拉取「最新一条未处理评论」，视觉与分镜评论按钮共用 CommentPreviewButton。
export default function AssetCommentButton({ projectId, category, name, count, onClick, t }: AssetCommentButtonProps) {
  const hasPending = count > 0;

  // 评论增删/处理后会触发 'asset-comments-updated'，借此重新拉取最新评论，
  // 不依赖未处理数量是否变化（处理一条+新增一条会让数量不变，但最新评论已变）。
  const [reloadTick, setReloadTick] = useState(0);
  useEffect(() => {
    const onUpdated = () => setReloadTick(v => v + 1);
    window.addEventListener('asset-comments-updated', onUpdated);
    return () => window.removeEventListener('asset-comments-updated', onUpdated);
  }, []);

  const [fetchedComment, setFetchedComment] = useState('');
  useEffect(() => {
    if (!hasPending) { setFetchedComment(''); return; }
    let cancelled = false;
    listAssetComments(projectId, category, name).then(comments => {
      if (cancelled) return;
      const pending = comments.filter(c => !c.isResolved);
      const latest = pending[pending.length - 1];
      setFetchedComment(latest?.content || '');
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [projectId, category, name, hasPending, count, reloadTick]);

  return (
    <CommentPreviewButton
      count={count}
      latestComment={fetchedComment}
      viewNewLabel={t('assetComments.viewNew')}
      buttonLabel={t('assetComments.button')}
      onClick={onClick}
      size="sm"
      testId="asset-comment-badge"
    />
  );
}
